import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Shield, Lock, Eye, Clock, 
  Trash2, Download, Server, Scale, AlertTriangle 
} from 'lucide-react';

export default function CharteConfidentialite() {
  return (
    <div className="min-h-screen bg-void">
      {/* Header */}
      <header className="border-b border-gray-800 py-4 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="font-orbitron text-xl font-bold text-cyan-400 glitch-subtle">
            L'EFFACEUR
          </Link>
          <Link to="/" className="text-gray-400 hover:text-cyan-400 flex items-center gap-2 text-sm font-mono">
            <ArrowLeft className="w-4 h-4" />
            Retour
          </Link>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Title */}
          <div className="flex items-center gap-4 mb-4">
            <Shield className="w-10 h-10 text-cyan-400" />
            <h1 className="font-orbitron text-3xl md:text-4xl font-bold text-white">
              CHARTE DE <span className="text-cyan-400">CONFIDENTIALITÉ</span>
            </h1>
          </div>
          <p className="text-magenta-400 font-mono text-lg mb-4">PROTOCOLE "ZÉRO TRACE"</p>
          <p className="text-gray-400 mb-12 leading-relaxed">
            Chez L'EFFACEUR, nous considérons vos données comme des actifs sensibles. 
            Notre mission est d'effacer vos contrats, pas de laisser une trace de votre vie privée.
          </p>
          
          <div className="space-y-8">
            {/* Section 1 */}
            <section className="glass-card p-8 border border-gray-800">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 bg-cyan-400 text-black flex items-center justify-center font-orbitron font-bold">1</span>
                <h2 className="font-orbitron text-xl font-bold text-white">RESPONSABILITÉ DU TRAITEMENT</h2>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                Le responsable du traitement des données est l'entreprise <strong className="text-cyan-400">L'EFFACEUR</strong>,
                immatriculée à Toulouse sous le SIRET 947 765 442 00019. Notre protocole de sécurité est supervisé par
                un Data Guardian interne, garantissant que chaque octet d'information est traité avec une rigueur chirurgicale.
              </p>
              <p className="text-gray-400 text-sm leading-relaxed">
                <strong className="text-cyan-400">Contact DPO / délégué à la protection des données :</strong>{' '}
                <a href="mailto:leffaceur@effaceurpro.fr?subject=Demande%20RGPD" className="text-cyan-400 hover:underline">
                  leffaceur@effaceurpro.fr
                </a>
                {' '}— pour toute question liée à vos données personnelles (accès, rectification, suppression, opposition, portabilité).
                Nous nous engageons à répondre dans un délai maximum de 30 jours.
              </p>
            </section>

            {/* Section 1 bis — Base légale */}
            <section className="glass-card p-8 border border-gray-800">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 bg-cyan-400 text-black flex items-center justify-center font-orbitron font-bold">1b</span>
                <h2 className="font-orbitron text-xl font-bold text-white">BASE LÉGALE DU TRAITEMENT</h2>
              </div>
              <p className="text-gray-400 mb-4">Conformément au RGPD (article 6), nos traitements reposent sur :</p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-3">
                  <span className="text-cyan-400 font-mono flex-shrink-0">(a)</span>
                  <span><strong className="text-cyan-400">Exécution du contrat</strong> — pour traiter votre mission (résiliation, transfert, succession).</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-400 font-mono flex-shrink-0">(b)</span>
                  <span><strong className="text-cyan-400">Obligation légale</strong> — conservation du mandat signé et des justificatifs de facturation pour les durées imposées par la loi.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-400 font-mono flex-shrink-0">(c)</span>
                  <span><strong className="text-cyan-400">Intérêt légitime</strong> — amélioration du service, détection de fraude, sécurité du compte.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-cyan-400 font-mono flex-shrink-0">(d)</span>
                  <span><strong className="text-cyan-400">Consentement</strong> — pour l'envoi d'emails informatifs non liés à votre mission. Vous pouvez le retirer à tout moment.</span>
                </li>
              </ul>
            </section>
            
            {/* Section 2 */}
            <section className="glass-card p-8 border border-gray-800">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 bg-cyan-400 text-black flex items-center justify-center font-orbitron font-bold">2</span>
                <h2 className="font-orbitron text-xl font-bold text-white">LES DONNÉES QUE NOUS "ASPIRONS"</h2>
              </div>
              <p className="text-gray-400 mb-4">Nous ne collectons que le strict nécessaire à l'exécution de votre mandat :</p>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start gap-3">
                  <Eye className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-cyan-400">Identité & Contact :</strong> Pour vous représenter officiellement.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Eye className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-cyan-400">Documents de Preuve :</strong> Actes de décès (Pack Succession), baux (Mission Déménagement), ou attestations diverses.</span>
                </li>
                <li className="flex items-start gap-3">
                  <Eye className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span><strong className="text-cyan-400">Données Financières :</strong> Uniquement les libellés de prélèvements de vos relevés (les montants et soldes peuvent être masqués par vos soins).</span>
                </li>
              </ul>
            </section>
            
            {/* Section 3 */}
            <section className="glass-card p-8 border border-gray-800">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 bg-cyan-400 text-black flex items-center justify-center font-orbitron font-bold">3</span>
                <h2 className="font-orbitron text-xl font-bold text-white">SÉCURITÉ & INTELLIGENCE ARTIFICIELLE</h2>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                Votre dossier est scanné par notre IA propriétaire pour identifier les failles de votre budget.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-magenta-400 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-300">
                    <strong className="text-magenta-400">Chiffrement :</strong> Toutes les pièces jointes sont chiffrées en <span className="font-mono text-cyan-400">AES-256</span> (standard militaire).
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <Server className="w-5 h-5 text-magenta-400 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-300">
                    <strong className="text-magenta-400">Traitement Éphémère :</strong> Notre IA analyse, extrait les données de résiliation, puis oublie.
                  </p>
                </div>
              </div>
            </section>
            
            {/* Section 4 */}
            <section className="glass-card p-8 border border-cyan-400/30 bg-cyan-400/5">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 bg-cyan-400 text-black flex items-center justify-center font-orbitron font-bold">4</span>
                <h2 className="font-orbitron text-xl font-bold text-white">PROTOCOLE DE RÉTENTION (30 JOURS)</h2>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="w-8 h-8 text-cyan-400 flex-shrink-0" />
                <div>
                  <p className="text-cyan-400 font-mono mb-2">// RÈGLE D'OR</p>
                  <p className="text-gray-300 leading-relaxed">
                    Contrairement aux administrations classiques, nous détestons l'archivage inutile. 
                    <strong> Toutes vos pièces justificatives (PDF, photos, captures d'écran) sont définitivement supprimées de nos serveurs 30 jours après la confirmation finale de la résiliation de vos contrats.</strong>
                  </p>
                  <p className="text-gray-400 text-sm mt-2">
                    Seule la preuve du mandat signé est conservée pour des raisons légales.
                  </p>
                </div>
              </div>
            </section>
            
            {/* Section 5 */}
            <section className="glass-card p-8 border border-gray-800">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 bg-cyan-400 text-black flex items-center justify-center font-orbitron font-bold">5</span>
                <h2 className="font-orbitron text-xl font-bold text-white">TRANSMISSION AUX TIERS & SOUS-TRAITANTS</h2>
              </div>
              <div className="bg-red-600/10 border border-red-600/30 p-4 mb-4">
                <p className="text-red-400 font-mono font-bold flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  NOUS NE VENDONS JAMAIS VOS DONNÉES.
                </p>
              </div>
              <p className="text-gray-300 leading-relaxed mb-4">
                Les seules entités recevant vos informations dans le cadre de votre mission sont :
              </p>
              <ul className="space-y-2 text-gray-300 mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-400 mt-1">•</span>
                  <span><strong className="text-cyan-400">Organismes mandatés</strong> : fournisseurs dont vous nous avez demandé la résiliation (énergie, télécom, banque, assurance, mutuelle, administration, etc.).</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-400 mt-1">•</span>
                  <span><strong className="text-cyan-400">Prestataire de paiement</strong> : Stripe (Irlande, conforme PCI-DSS) pour le traitement sécurisé des paiements.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-400 mt-1">•</span>
                  <span><strong className="text-cyan-400">Prestataire d'emails transactionnels</strong> : Brevo (France) pour l'envoi des confirmations de mission et mandats.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-400 mt-1">•</span>
                  <span><strong className="text-cyan-400">Hébergement</strong> : MongoDB Atlas (UE) pour la base de données, Google Cloud Platform pour l'infrastructure.</span>
                </li>
              </ul>
              <p className="text-gray-400 text-sm leading-relaxed">
                <strong className="text-cyan-400">Transferts hors Union européenne</strong> : certains sous-traitants (ex. GCP) peuvent traiter
                des données en dehors de l'UE. Ces transferts sont encadrés par des <em>Clauses Contractuelles Types</em> validées par la
                Commission européenne, garantissant un niveau de protection équivalent au RGPD.
              </p>
            </section>
            
            {/* Section 6 */}
            <section className="glass-card p-8 border border-gray-800">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 bg-cyan-400 text-black flex items-center justify-center font-orbitron font-bold">6</span>
                <h2 className="font-orbitron text-xl font-bold text-white">VOS DROITS (RGPD)</h2>
              </div>
              <p className="text-gray-400 mb-4">Vous gardez le contrôle total sur votre identité numérique. Conformément aux articles 15 à 22 du RGPD, vous disposez des droits suivants :</p>
              <div className="space-y-4">
                <div className="flex items-start gap-3 p-4 bg-gray-800/30 border border-gray-700">
                  <Trash2 className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Droit à l'effacement immédiat (art. 17)</p>
                    <p className="text-gray-400 text-sm">Un simple clic dans votre espace client déclenche l'autodestruction de vos documents (bouton "Panic Room").</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-gray-800/30 border border-gray-700">
                  <Download className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Droit d'accès et de portabilité (art. 15 & 20)</p>
                    <p className="text-gray-400 text-sm">Vous pouvez demander un export complet de toutes vos données, dans un format structuré et réutilisable.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-gray-800/30 border border-gray-700">
                  <Eye className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Droit de rectification (art. 16)</p>
                    <p className="text-gray-400 text-sm">Vous pouvez modifier à tout moment les informations inexactes vous concernant depuis votre espace "Mon Compte".</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-gray-800/30 border border-gray-700">
                  <AlertTriangle className="w-5 h-5 text-magenta-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Droit d'opposition et de limitation (art. 18 & 21)</p>
                    <p className="text-gray-400 text-sm">Vous pouvez vous opposer au traitement pour motif légitime ou demander la limitation temporaire de celui-ci.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-gray-800/30 border border-gray-700">
                  <Scale className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Droit de réclamation auprès de la CNIL</p>
                    <p className="text-gray-400 text-sm">
                      Si vous estimez que le traitement de vos données n'est pas conforme, vous pouvez introduire une réclamation auprès de la CNIL :{' '}
                      <a href="https://www.cnil.fr/fr/plaintes" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">www.cnil.fr/fr/plaintes</a>.
                    </p>
                  </div>
                </div>
              </div>
              <p className="text-gray-500 text-xs italic mt-4">
                Toute demande liée à ces droits doit être adressée à{' '}
                <a href="mailto:leffaceur@effaceurpro.fr?subject=Demande%20RGPD" className="text-cyan-400 hover:underline">leffaceur@effaceurpro.fr</a>.
                Nous nous engageons à vous répondre sous 30 jours maximum, conformément à l'article 12 du RGPD.
              </p>
            </section>

            {/* Section 6 bis — Cookies */}
            <section className="glass-card p-8 border border-gray-800">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 bg-cyan-400 text-black flex items-center justify-center font-orbitron font-bold">6b</span>
                <h2 className="font-orbitron text-xl font-bold text-white">COOKIES & TRACEURS</h2>
              </div>
              <p className="text-gray-300 leading-relaxed mb-3">
                Notre site utilise uniquement des cookies strictement nécessaires au fonctionnement du service :
              </p>
              <ul className="space-y-2 text-gray-300 mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-400 mt-1">•</span>
                  <span><strong className="text-cyan-400">Cookies d'authentification</strong> (token JWT, session) — indispensables pour maintenir votre connexion.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-400 mt-1">•</span>
                  <span><strong className="text-cyan-400">Cookies de préférences</strong> — mémorisation de votre choix d'acceptation des CGV, sélection de pack en cours.</span>
                </li>
              </ul>
              <p className="text-gray-400 text-sm leading-relaxed">
                Aucun cookie publicitaire, de tracking comportemental ou de mesure d'audience n'est déployé sans votre consentement explicite.
                Vous pouvez gérer les cookies depuis les paramètres de votre navigateur.
              </p>
            </section>
            
            {/* Section 7 */}
            <section className="glass-card p-8 border border-gray-800">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-8 bg-cyan-400 text-black flex items-center justify-center font-orbitron font-bold">7</span>
                <h2 className="font-orbitron text-xl font-bold text-white">JURIDICTION & LITIGES</h2>
              </div>
              <div className="flex items-start gap-3">
                <Scale className="w-6 h-6 text-magenta-400 flex-shrink-0" />
                <p className="text-gray-300 leading-relaxed">
                  L'EFFACEUR est couvert par une <strong className="text-magenta-400">Assurance Responsabilité Civile Professionnelle</strong>. 
                  En cas de litige, seul le <strong className="text-cyan-400">droit français</strong> est applicable, 
                  sous la juridiction des <strong className="text-cyan-400">tribunaux de Toulouse</strong>.
                </p>
              </div>
            </section>
          </div>
          
          {/* Footer */}
          <div className="mt-12 text-center">
            <p className="text-gray-500 text-sm font-mono">
              Dernière mise à jour : Décembre 2024
            </p>
            <Link 
              to="/mentions-legales" 
              className="text-cyan-400 hover:underline text-sm mt-4 inline-block"
            >
              Voir les Mentions Légales →
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
